import type { BaseCampaign } from '@feathr/blackbox';
import { moment } from '@feathr/hooks';

/**
 * Sum the fixed budgets of every campaign that was active at any point within
 * the report window.
 *
 * A campaign is considered active during the window when its `date_start` is on
 * or before the window end and its `date_end` is on or after the window start.
 * A falsy `date_end` (null, undefined, or empty string) is treated as
 * still-running, matching `BaseCampaign.isAfterDateEnd`, so such campaigns are
 * included whenever they started on or before the window end. This overlap test
 * replaces the previous containment test, which dropped campaigns that started
 * before the window but were still active during it.
 *
 * Only campaigns whose `exposure_settings.target_type` is `'fixed_budget'`
 * contribute their `target_value` (defaulting to 0 when missing).
 *
 * Used by both the flight and event reports' Spend performance / Budget card.
 *
 * @param campaigns - The report's campaigns.
 * @param start - The report window start as an ISO 8601 string.
 * @param end - The report window end as an ISO 8601 string.
 * @returns The total fixed budget of all overlapping campaigns.
 */
export function calculateActiveCampaignBudget(
  campaigns: BaseCampaign[],
  start: string,
  end: string,
): number {
  const reportStartMoment = moment.utc(start, moment.ISO_8601);
  const reportEndMoment = moment.utc(end, moment.ISO_8601);

  return campaigns
    .filter((cpn) => {
      const dateStartMoment = moment.utc(cpn.get('date_start'), moment.ISO_8601);
      const dateEnd = cpn.get('date_end') as string | null | undefined;
      const isStillRunning = !dateEnd;
      return (
        dateStartMoment.isSameOrBefore(reportEndMoment) &&
        (isStillRunning || moment.utc(dateEnd, moment.ISO_8601).isSameOrAfter(reportStartMoment))
      );
    })
    .reduce((acc, cpn) => {
      const exposureSettings = cpn.get('exposure_settings');
      if (exposureSettings.target_type === 'fixed_budget') {
        acc += exposureSettings.target_value || 0;
      }
      return acc;
    }, 0);
}
